import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plane } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../lib/api.ts';
import { useItUsers } from '../lib/it.ts';
import { sessionKey, useSession } from '../lib/session.ts';
import { Button } from './ui/button.tsx';
import { Field, Input, Select } from './ui/form.tsx';
import { Alert, Card } from './ui/misc.tsx';
import { useToast } from './ui/toast.tsx';

const formatDay = (d: string) =>
  new Date(`${d}T12:00:00Z`).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });

/**
 * Settings, for IT: leave dates and who covers. While on leave, new tickets
 * go to the cover, you still get copies, and staff see who's covering.
 */
export function LeaveCard() {
  const { user, isIt } = useSession();
  const client = useQueryClient();
  const toast = useToast();
  // Shares the cache with the assignment picker (lib/it.ts).
  const itUsers = useItUsers();
  const colleagues = { data: itUsers.data?.filter((u) => u.id !== user?.id) };
  const away = user?.away;
  const [from, setFrom] = useState(away?.from ?? '');
  const [until, setUntil] = useState(away?.until ?? '');
  const [cover, setCover] = useState(away?.coverUserId ? String(away.coverUserId) : '');
  const [error, setError] = useState<string>();

  const save = useMutation({
    mutationFn: (body: object | null) => api.put('/api/me/away', body),
    onSuccess: (_, body) => {
      void client.invalidateQueries({ queryKey: sessionKey });
      void client.invalidateQueries({ queryKey: ['office-status'] });
      setError(undefined);
      if (!body) {
        setFrom('');
        setUntil('');
        setCover('');
      }
      toast({ tone: 'success', title: body ? 'Leave saved' : 'Welcome back: leave cleared' });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });
  if (!isIt || !user) return null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!from || !until) return setError('Choose the first and last day of your leave.');
    save.mutate({ from, until, coverUserId: cover ? Number(cover) : null });
  };

  return (
    <Card className="p-5 sm:p-6">
      <h2 className="flex items-center gap-2 font-semibold">
        <Plane className="size-5 text-muted" aria-hidden />
        Leave and cover
      </h2>
      <p className="mt-1 text-sm text-muted">
        While you’re away, new tickets go to whoever covers for you and staff see who that is. You still get
        copies of the alerts, so you can help remotely.
      </p>
      {away && (
        <Alert tone="info" className="mt-4">
          Leave set from {formatDay(away.from)} to {formatDay(away.until!)}.
        </Alert>
      )}
      <form onSubmit={submit} noValidate className="mt-4 space-y-4">
        {error && <Alert tone="error">{error}</Alert>}
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="First day">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="Last day">
            <Input
              type="date"
              value={until}
              min={from || undefined}
              onChange={(e) => setUntil(e.target.value)}
            />
          </Field>
          <Field label="Covering">
            <Select
              value={cover}
              onChange={(e) => setCover(e.target.value)}
              placeholder="Nobody (remote only)"
              options={(colleagues.data ?? []).map((u) => ({
                value: String(u.id),
                label: u.name || u.email,
              }))}
            />
          </Field>
        </div>
        {colleagues.data?.length === 0 && (
          <p className="text-sm text-muted">
            To have someone cover, first add them as a technician under Users.
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" loading={save.isPending}>
            Save leave
          </Button>
          {away && (
            <Button variant="ghost" onClick={() => save.mutate(null)} disabled={save.isPending}>
              I’m back: clear leave
            </Button>
          )}
        </div>
      </form>
    </Card>
  );
}
